import { useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, GraduationCap, TrendingUp, PiggyBank, Shield, BarChart3, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface PathArticle {
  title: string;
  slug: string;
}

interface LearningPath {
  id: string;
  icon: React.ElementType;
  title: string;
  description: string;
  articles: PathArticle[];
  color: string;
}

const learningPaths: LearningPath[] = [
  {
    id: "retirement",
    icon: Shield,
    title: "Retirement Planning",
    description: "Build a sustainable retirement strategy — from the 4% rule debate to pension planning and compounding.",
    color: "bg-blue-500/10 border-blue-500/30 text-blue-600 dark:text-blue-400",
    articles: [
      { title: "The 4% Rule is Dead: Rethinking Retirement in a High-Inflation World", slug: "4-percent-rule-dead-rethinking-retirement-high-inflation" },
      { title: "Retirement Planning in Bangladesh 2026: Build a Pension You Can Live On", slug: "retirement-planning-bangladesh-gpf-provident-fund-2026" },
      { title: "The 15-15-15 Rule: A Simple Formula to Retire in Your 40s", slug: "15-15-15-rule-retire-early-40s" },
      { title: "The Power of Compounding: ৳5,000 SIP to a Crore", slug: "power-of-compounding-sip-crore-bangladesh" },
    ],
  },
  {
    id: "budgeting",
    icon: PiggyBank,
    title: "Data-Driven Budgeting",
    description: "Master budgeting with data analytics — from zero-based methods to predictive ML-powered forecasting.",
    color: "bg-green-500/10 border-green-500/30 text-green-600 dark:text-green-400",
    articles: [
      { title: "Zero-Based Budgeting: Take Full Control of Your Monthly Salary", slug: "zero-based-budgeting-monthly-salary-bangladesh-2026" },
      { title: "Predictive Budgeting: Use Machine Learning to Forecast Expenses", slug: "predictive-budgeting-machine-learning-forecast-expenses" },
      { title: "5 Smart Budgeting Tips to Take Control of Your Finances", slug: "5-smart-budgeting-tips-to-take-control-of-your-finances-in-2026" },
      { title: "7 Anti-Budget Hacks to Save Your First ৳1,00,000", slug: "anti-budget-hacks-save-100k-bdt" },
      { title: "The Psychology of Spending: Why Your Brain Sabotages Your Budget", slug: "psychology-of-spending" },
    ],
  },
  {
    id: "investing",
    icon: TrendingUp,
    title: "Smart Investing",
    description: "Navigate equities, bonds, gold, and fixed deposits with data-driven analysis and risk management.",
    color: "bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-400",
    articles: [
      { title: "Dollar-Cost Averaging for Bangladeshi Investors", slug: "dollar-cost-averaging-strategy-bangladesh-investors" },
      { title: "Gold Investment in Bangladesh 2026: The Smart Inflation Hedge", slug: "gold-investment-guide-bangladesh-2026" },
      { title: "Equity vs. Debt: Building a Balanced Portfolio for 2026", slug: "equity-vs-debt-balanced-portfolio-2026-bangladesh-market" },
      { title: "Sanchayapatra vs. Mutual Funds: Where to Put Your Savings?", slug: "sanchayapatra-vs-mutual-funds-2026" },
      { title: "Stock Market vs. Fixed Deposits: Where to Put Your Money?", slug: "stock-market-vs-fixed-deposits-2026" },
    ],
  },
  {
    id: "personal-finance",
    icon: BarChart3,
    title: "Personal Finance Mastery",
    description: "From emergency funds and debt freedom to freelancer finances — actionable guides for every life stage.",
    color: "bg-purple-500/10 border-purple-500/30 text-purple-600 dark:text-purple-400",
    articles: [
      { title: "The 2026 Emergency Fund Guide: How Much Is Actually Safe?", slug: "emergency-fund-guide-2026-bangladesh" },
      { title: "Debt-Free Roadmap: Pay Off Loans & Credit Cards Faster", slug: "debt-free-roadmap-pay-off-loans-faster" },
      { title: "Freelancer Finance: Manage Money with Irregular Income", slug: "freelancer-finance-management-bangladesh-2026" },
      { title: "5 Automated Money Habits That Save You 10 Hours a Month", slug: "automated-money-habits-save-time" },
      { title: "Life and Health Insurance: The Safety Net Families Are Missing", slug: "life-health-insurance-planning-bangladesh-2026" },
    ],
  },
];

export default function ResourceCenter() {
  const [activePath, setActivePath] = useState(learningPaths[0].id);
  const current = learningPaths.find((p) => p.id === activePath) || learningPaths[0];

  return (
    <section aria-labelledby="resource-heading" className="container mx-auto px-4 py-20">
      <div className="text-center mb-10">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 mb-4">
          <GraduationCap className="h-7 w-7 text-primary" aria-hidden="true" />
        </div>
        <h2 id="resource-heading" className="text-3xl lg:text-4xl font-bold text-foreground mb-3">
          Resource Center &amp; Learning Paths
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          30+ original research articles — data-driven insights from 11 years of financial expertise.
          Grouped into structured learning paths so you can master each topic step by step.
        </p>
      </div>

      {/* Path selector tabs */}
      <div className="flex flex-wrap justify-center gap-2 mb-8">
        {learningPaths.map((path) => (
          <button
            key={path.id}
            onClick={() => setActivePath(path.id)}
            className={cn(
              "flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all",
              activePath === path.id
                ? "bg-primary text-primary-foreground border-primary shadow-md"
                : "bg-card border-border/50 text-muted-foreground hover:border-primary/40 hover:text-foreground"
            )}
          >
            <path.icon className="h-4 w-4" aria-hidden="true" />
            {path.title}
          </button>
        ))}
      </div>

      {/* Active path content */}
      <div className={cn("rounded-2xl border p-6 max-w-3xl mx-auto", current.color)}>
        <div className="flex items-start gap-3 mb-5">
          <div className="shrink-0 p-2.5 rounded-xl bg-background/80 border border-border/50">
            <current.icon className="h-6 w-6" aria-hidden="true" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-foreground">{current.title}</h3>
            <p className="text-sm text-muted-foreground mt-0.5">{current.description}</p>
          </div>
        </div>

        <ol className="space-y-1.5">
          {current.articles.map((article, idx) => (
            <li key={article.slug}>
              <Link
                to={`/blog/${article.slug}`}
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 bg-background/60 border border-border/30 hover:border-primary/40 hover:bg-background transition-colors group"
              >
                <span className="shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                  {idx + 1}
                </span>
                <span className="flex-1 text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                  {article.title}
                </span>
                <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary shrink-0" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ol>

        <div className="mt-5 flex items-center justify-between">
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <BookOpen className="h-3.5 w-3.5" aria-hidden="true" />
            {current.articles.length} articles in this path
          </span>
          <Link
            to="/blog"
            className="text-xs font-medium text-primary hover:underline flex items-center gap-1"
          >
            Browse all articles
            <ChevronRight className="h-3 w-3" aria-hidden="true" />
          </Link>
        </div>
      </div>

      <p className="text-center text-sm text-muted-foreground mt-8 max-w-xl mx-auto">
        Every article is original research by{" "}
        <Link to="/about" className="text-primary hover:underline font-medium">Rakibul Hossen</Link>,
        combining 11+ years of financial industry experience with data science methodology.
      </p>
    </section>
  );
}
